import { router } from "@inertiajs/react"
import {
  ChevronLeft,
  ChevronRight,
  MessageTextCircle01,
  Phone01,
} from "@untitledui/icons"
import type { CSSProperties } from "react"
import { useEffect, useRef, useState } from "react"

import { Carousel, useCarousel } from "@/components/ui/carousel"
import { cn } from "@/lib/utils"
import { onwardsReferralsPath } from "@/routes"

import { FOCUS_RING } from "./jacket-chrome"
import { OutroLabelArt, catalogNumber } from "./outro-label-art"
import { PaperQr } from "./paper-qr"
import type { OutroCandidate, OutroSlate } from "./paper-types"
import { DISPLAY, chromeLabel, kicker, linerNotes } from "./type-voices"

/* Local configuration */
const IS_DISPLAY_THUMBNAIL = true
// Auto-continue countdown is parked until its referral wiring is agreed; a
// millisecond value re-enables the bar on the continuation card. Must stay
// null (not 0) while off — `0 &&` renders a literal "0" in JSX.
const TIME_CONTINUATION_COUNTDOWN: number | null = null
const CLIP_EXPLORATION_CARD = 4

/* Local helper functions */

// Split a list into fixed-size pages 2×2 carousel(4 tiles each).
function chunk<T>(items: T[], size: number): T[][] {
  const pages: T[][] = []
  for (let i = 0; i < items.length; i += size) {
    pages.push(items.slice(i, i + size))
  }
  return pages
}

// The redirect target is resolved server-side (gate + consent re-checked at
// click time), so a step only ever posts its impression id + chosen channel.
function refer(candidate: OutroCandidate) {
  router.post(onwardsReferralsPath(), {
    impression_id: candidate.impression_id,
    channel: candidate.channel ?? "web",
  })
}

// The card button's aria-label overrides its content, so the phone number
// must ride along for it to be announced at all.
function cardAriaLabel(candidate: OutroCandidate) {
  const base = `${candidate.action_label} — ${candidate.title}`
  return candidate.phone_target
    ? `${base} — or call ${candidate.phone_target.replace(/^tel:/, "")}`
    : base
}

/* Pre CSS configuration */

const cardKicker: CSSProperties = {
  ...chromeLabel,
  color: "rgb(var(--batyr-ink-rgb) / 0.55)",
}
const cardMeta: CSSProperties = {
  ...chromeLabel,
  color: "rgb(var(--batyr-ink-rgb) / 0.5)",
}
const cardTitle: CSSProperties = {
  fontFamily: DISPLAY,
  fontWeight: 700,
  fontSize: 22,
  lineHeight: 1.05,
  letterSpacing: "-0.01em",
  color: "var(--color-batyr-ink)",
}
const tileTitle: CSSProperties = {
  ...cardTitle,
  fontSize: 17,
}
// The action reads in the curator's red-pencil — the mark, not a button.
const cardAction: CSSProperties = {
  ...kicker,
  color: "var(--color-batyr-red-pencil)",
}

/**
 * PaperOutroSlate — the two rows of the Onwards slate: the promoted
 * continuation full-width on its own row, then the exploration candidates as
 * a responsive card group underneath. Both rows share the same `w-full`
 * parent, so neither ever runs a different max width than the other.
 */
export function PaperOutroSlate({
  slate,
  className,
  disabledTriggerAutoPlay = false,
}: {
  slate?: OutroSlate | null
  disabledTriggerAutoPlay?: boolean
  className?: string
}) {
  if (!slate) return null
  const { continuation, exploration } = slate
  return (
    <div className={cn("@container flex w-full flex-col gap-4", className)}>
      {continuation && (
        <ContinuationCard
          candidate={continuation}
          disabledTriggerAutoPlay={disabledTriggerAutoPlay}
        />
      )}
      {exploration.length > 0 && (
        <ExplorationCardGroup candidates={exploration} />
      )}
    </div>
  )
}

/**
 * ExplorationCardGroup - a component grid that holds a ExplorationTile. Once
 * the candidate count crosses CLIP_EXPLORATION_CARD the grid would wrap into
 * an unwieldy number of rows, so the group swaps to a swipeable carousel that
 * pages through the tiles instead of stacking them.
 */
function ExplorationCardGroup({
  candidates,
}: {
  candidates: OutroCandidate[]
}) {
  const isClipped = candidates.length > CLIP_EXPLORATION_CARD

  return (
    <div className="flex flex-col gap-2">
      <span className="text-batyr-ink/60 linerNotes text-sm">More Ways on</span>
      {isClipped ? (
        <ExplorationCardCarousel candidates={candidates} />
      ) : (
        <div className="grid w-full grid-cols-2 gap-3 @lg:grid-cols-3 @3xl:grid-cols-4">
          {candidates.map((candidate) => (
            <ExplorationTile
              key={candidate.impression_id}
              candidate={candidate}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * ExplorationTile — one cell in the grid.
 */
function ExplorationTile({ candidate }: { candidate: OutroCandidate }) {
  const kickerText = "keep exploring"

  return (
    <OutroButtonCard
      onClick={() => refer(candidate)}
      ariaLabel={cardAriaLabel(candidate)}
      className="h-full"
    >
      <OutroImage candidate={candidate} />

      <div className="bg-surface-elevated flex h-full w-full flex-col items-start gap-3 px-4 py-3">
        <div className="flex w-full items-baseline justify-between gap-2">
          <span style={cardKicker}>{kickerText}</span>
          <span style={cardMeta}>{catalogNumber(candidate.key)}</span>
        </div>

        <div className="flex w-full flex-1 flex-col gap-2">
          <div className="flex flex-col gap-2">
            <span style={tileTitle}>{candidate.title}</span>
          </div>
        </div>

        {candidate.channel === "phone" && candidate.phone_target && (
          <PhoneHandoff target={candidate.phone_target} compact />
        )}

        <div className="flex w-full flex-col">
          <div className="flex flex-row">
            <ContractInfo contract={candidate.channel ?? undefined} />
          </div>
          <div className="flex">
            <span style={cardMeta}>
              {candidate.action_label}
              {candidate.needs_consent ? " — you choose what to share" : ""}
            </span>
          </div>
        </div>
      </div>
    </OutroButtonCard>
  )
}

/**
 * ContinuationCard — the promoted step, full width on its own row.
 */
function ContinuationCard({
  candidate,
  disabledTriggerAutoPlay,
}: {
  candidate: OutroCandidate
  disabledTriggerAutoPlay?: boolean
}) {
  const kickerText = "keep going"
  const [isPaused, setIsPaused] = useState(false)

  return (
    // Past @xl (the Lab's desktop showcase; the player column never gets
    // there) the promoted card turns horizontal — image as a left plate,
    // text beside it — instead of stacking a viewport-tall 16:9 band.
    <OutroButtonCard
      onClick={() => refer(candidate)}
      onClickSecondary={() => setIsPaused((prev) => !prev)}
      labelSecondaryBtn={isPaused ? "Resume" : "Stay here"}
      ariaLabel={cardAriaLabel(candidate)}
      className="@xl:flex-row @xl:items-stretch"
    >
      <OutroImage
        candidate={candidate}
        className="@xl:aspect-auto @xl:w-[45%] @xl:self-stretch"
      />

      <div className="bg-surface-elevated flex h-full w-full flex-col items-start gap-3 px-5 py-4 @xl:min-w-0 @xl:flex-1">
        <div className="flex w-full items-baseline justify-between gap-3">
          <span style={cardAction}>{kickerText}</span>
          <span style={cardMeta}>{catalogNumber(candidate.key)}</span>
        </div>

        <div className="flex w-full flex-1 flex-col gap-2">
          <div className="flex flex-col gap-2">
            <span style={cardTitle}>{candidate.title}</span>
            {candidate.summary && (
              <span style={linerNotes}>{candidate.summary}</span>
            )}
          </div>
          {TIME_CONTINUATION_COUNTDOWN && (
            <div className="flex flex-col">
              <OutroCountdownProgressBar
                delay={TIME_CONTINUATION_COUNTDOWN}
                isPaused={isPaused}
                onTimeup={() => {
                  if (disabledTriggerAutoPlay) return
                  /* TODO: Here's for injecting business logic for precess next */
                  return
                }}
              />
              <OutroCountDownTimer
                delay={TIME_CONTINUATION_COUNTDOWN}
                isPaused={isPaused}
              />
            </div>
          )}
        </div>

        {candidate.channel === "phone" && candidate.phone_target && (
          <PhoneHandoff target={candidate.phone_target} />
        )}

        <div className="flex w-full flex-row">
          <div className="flex flex-row">
            <ContractInfo contract={candidate.channel ?? undefined} />
          </div>
          <div className="flex flex-1" />
          <div className="flex items-center justify-center">
            <div className="flex">
              <span className="text-right" style={cardAction}>
                {candidate.action_label}
                {candidate.needs_consent ? " — you choose what to share" : ""}
              </span>
            </div>
            <div className="text-[var(--color-batyr-red-pencil)]">{" →"}</div>
          </div>
        </div>
      </div>
    </OutroButtonCard>
  )
}

/**
 * Contract info — an div wrapper component for contract infomation.
 */
function ContractInfo({
  contract,
  className,
}: {
  contract?: string
  className?: string
}) {
  const ContractIcons: Record<string, React.ReactNode> = {
    web: <MessageTextCircle01 className="size-3" aria-hidden="true" />,
    phone: <Phone01 className="size-3" aria-hidden="true" />,
  }
  return contract ? (
    <div className={cn("inline-flex items-center gap-1", className)}>
      <div className="flex">
        <span style={cardMeta}>
          {ContractIcons[contract.toLowerCase()] ?? null}
        </span>
      </div>
      <div className="flex">
        <span style={cardMeta}>{contract}</span>
      </div>
    </div>
  ) : null
}

/**
 * PhoneHandoff — scan-to-call for phone-channel candidates: an ink QR of the
 * tel: target beside the number in print. Bridges a projected/desktop outro
 * to the viewer's actual phone. Caveat, deliberate: a scan dials directly and
 * writes no referral row (the on-card tap path still logs); a signed
 * short-link handoff could close that gap later. The QR is aria-hidden — the
 * printed number is the accessible and no-camera path.
 */
function PhoneHandoff({
  target,
  compact,
}: {
  target: string
  compact?: boolean
}) {
  const number = target.replace(/^tel:/, "")
  return (
    <div className="flex items-center gap-2.5">
      <PaperQr data={target} size={compact ? 48 : 64} />
      <div className="flex flex-col">
        <span style={cardKicker}>scan to call</span>
        <span style={cardMeta}>{number}</span>
      </div>
    </div>
  )
}

/**
 * OutroImage — the card's 16:9 image band: the destination's curated card
 * image when one is attached, else its deterministic letterpress label
 * (seeded by the stable destination key, so it never reshuffles between
 * visits). Decorative either way — the title carries the name just below.
 */
function OutroImage({
  candidate,
  className,
}: {
  candidate: OutroCandidate
  className?: string
}) {
  const fp = candidate.image_focal_point
  return IS_DISPLAY_THUMBNAIL ? (
    <div
      className={cn("aspect-[16/9] w-full shrink-0 overflow-hidden", className)}
    >
      {candidate.image_url ? (
        <img
          src={candidate.image_url}
          alt=""
          loading="lazy"
          className="h-full w-full object-cover"
          style={fp ? { objectPosition: `${fp.x}% ${fp.y}%` } : undefined}
        />
      ) : (
        <OutroLabelArt seed={candidate.key} title={candidate.title} />
      )}
    </div>
  ) : (
    <></>
  )
}

/**
 * OutroCountdownProgressBar — a progresssion bar countdown indicator.
 */
function OutroCountdownProgressBar({
  delay,
  onTimeup,
  isPaused,
  className,
}: {
  delay: number
  onTimeup: () => void
  isPaused?: boolean
  className?: string
}) {
  const barRef = useRef<HTMLDivElement>(null)
  const elapsedRef = useRef(0)
  const startedAtRef = useRef<number | null>(null)
  const completedRef = useRef(false)

  // Keep the latest `onTimeup` in a ref rather than an effect's deps: an
  // inline arrow prop gets a new identity every render, and depending on it
  // directly would restart the whole countdown (a fresh full bar) on any
  // unrelated re-render of the parent.
  const onTimeupRef = useRef(onTimeup)
  useEffect(() => {
    onTimeupRef.current = onTimeup
  })

  useEffect(() => {
    const bar = barRef.current
    if (completedRef.current || !bar) return

    const jumpTo = (percent: number) => {
      bar.style.transition = "none"
      bar.style.width = `${percent}%`
      void bar.offsetWidth
    }
    const animateTo = (percent: number, transitionMs: number) => {
      bar.style.transition = `width ${transitionMs}ms linear`
      bar.style.width = `${percent}%`
    }

    if (delay <= 0) {
      completedRef.current = true
      jumpTo(100)
      onTimeupRef.current()
      return
    }

    const frozenPercent = Math.min(100, (elapsedRef.current / delay) * 100)
    jumpTo(frozenPercent)

    if (isPaused) return

    const remaining = Math.max(delay - elapsedRef.current, 0)
    startedAtRef.current = Date.now()
    animateTo(100, remaining)

    const timeoutId = window.setTimeout(() => {
      completedRef.current = true
      onTimeupRef.current()
    }, remaining)

    return () => {
      window.clearTimeout(timeoutId)
      if (startedAtRef.current !== null) {
        elapsedRef.current += Date.now() - startedAtRef.current
        startedAtRef.current = null
      }
    }
  }, [delay, isPaused])

  return (
    <div
      className={cn(
        "bg-batyr-ink/10 h-1 w-full overflow-hidden rounded-sm",
        className,
      )}
    >
      <div
        ref={barRef}
        className="bg-batyr-red-pencil h-full"
        style={{ width: "0%" }}
      />
    </div>
  )
}

/**
 * OutroCountDownTimer — an numberic countdown indicator.
 */
function OutroCountDownTimer({
  delay = 10000,
  isPaused,
}: {
  delay?: number
  isPaused?: boolean
}) {
  const [timeLeft, setTimeLeft] = useState(delay)

  useEffect(() => {
    if (timeLeft <= 0 || isPaused) return

    const timerId = setInterval(() => {
      setTimeLeft((prevTime) => Math.max(0, prevTime - 1000))
    }, 1000)

    return () => clearInterval(timerId)
  }, [timeLeft, isPaused])

  return (
    <div>
      <span style={cardMeta}>
        CONTINUES IN {Math.floor((timeLeft / 1000) % 60)} s
      </span>
    </div>
  )
}

/**
 * OutroButtonCard — the card-as-button shell. The whole card is one `<button>`;
 * the optional secondary action is a sibling `<button>` below it, never nested
 * inside, so both stay keyboard-reachable.
 */
function OutroButtonCard({
  children,
  onClick,
  ariaLabel,
  className,
  classNameWrapper,
  labelSecondaryBtn,
  onClickSecondary,
}: {
  children: React.ReactNode
  onClick: () => void
  ariaLabel?: string
  className?: string
  classNameWrapper?: string
  labelSecondaryBtn?: string
  onClickSecondary?: () => void
}) {
  return (
    // FOCUS_RING is a box-shadow on the buttons, so the wrapper must not
    // clip — rounding and overflow-hidden live on the card button instead.
    <div
      className={cn(
        "bg-batyr-ink/15 flex w-full flex-col items-start rounded-[4px] text-left shadow-[0px_3px_6px_0px_rgba(8,_11,_14,_0.06)]",
        classNameWrapper,
      )}
    >
      <button
        type="button"
        aria-label={ariaLabel}
        onClick={onClick}
        className={cn(
          "flex h-full w-full cursor-pointer flex-col overflow-hidden rounded-[4px] text-left",
          FOCUS_RING,
          className,
        )}
      >
        {children}
      </button>
      {TIME_CONTINUATION_COUNTDOWN && onClickSecondary && labelSecondaryBtn && (
        <button
          type="button"
          onClick={onClickSecondary}
          className={cn(
            "flex h-12 w-full cursor-pointer items-center justify-center",
            FOCUS_RING,
          )}
        >
          <span className="text-tertiary text-sm font-bold">
            {labelSecondaryBtn}
          </span>
        </button>
      )}
    </div>
  )
}

/**
 * ExplorationCardCarousel — the clipped view of the exploration tiles
 */
function ExplorationCardCarousel({
  candidates,
}: {
  candidates: OutroCandidate[]
}) {
  const hasTrigger = false
  const trigger = cn(
    FOCUS_RING,
    "bg-surface-elevated text-batyr-ink/70 absolute top-1/2 z-10 flex size-8",
    "-translate-y-1/2 cursor-pointer items-center justify-center rounded-full",
    "shadow-sm transition-opacity disabled:pointer-events-none disabled:opacity-0",
  )

  const pages = chunk(candidates, CLIP_EXPLORATION_CARD)

  return (
    <Carousel.Root
      className="flex flex-col gap-2"
      opts={{ align: "start", containScroll: "trimSnaps" }}
    >
      <div className="relative">
        <Carousel.Content className="gap-3">
          {pages.map((page, pageIndex) => (
            <Carousel.Item
              key={page[0]?.impression_id ?? pageIndex}
              className="basis-full"
            >
              <div className="grid grid-cols-2 gap-3 pb-6">
                {page.map((candidate) => (
                  <ExplorationTile
                    key={candidate.impression_id}
                    candidate={candidate}
                  />
                ))}
              </div>
            </Carousel.Item>
          ))}
        </Carousel.Content>
        {hasTrigger && (
          <>
            <Carousel.PrevTrigger className={cn(trigger, "left-1")}>
              <ChevronLeft className="size-5" aria-hidden="true" />
            </Carousel.PrevTrigger>
            <Carousel.NextTrigger className={cn(trigger, "right-1")}>
              <ChevronRight className="size-5" aria-hidden="true" />
            </Carousel.NextTrigger>
          </>
        )}
      </div>

      <ExplorationCarouselDots />
    </Carousel.Root>
  )
}

/**
 * ExplorationCarouselDots — pagination indicators.
 */
function ExplorationCarouselDots() {
  const { scrollSnaps, selectedIndex, api } = useCarousel()

  if (scrollSnaps.length <= 1) return null

  return (
    <div className="flex justify-center gap-1.5 pt-1">
      {scrollSnaps.map((_, index) => (
        <button
          key={index}
          type="button"
          aria-label={`Go to slide ${index + 1}`}
          aria-current={selectedIndex === index ? "true" : undefined}
          onClick={() => api?.scrollTo(index)}
          className={cn(
            "size-1.5 rounded-full transition-colors",
            selectedIndex === index ? "bg-batyr-ink/70" : "bg-batyr-ink/25",
          )}
        />
      ))}
    </div>
  )
}
